/**
 * The four WaitGo states.
 *
 * WaitGo itself is still only 'Go' or 'Wait' — it is varchar(4) in the
 * database and widening it would touch every consumer. The caveat is composed
 * here from the dependency counts that travel alongside it.
 *
 *   Wait                red     hard dependencies outstanding
 *   Wait with caveats   red     + amber count badge
 *   Go                  green   nothing outstanding
 *   Go with caveats     green   + amber count badge — only bypassable left, so
 *                               this can proceed, but only by applying the
 *                               bypass; or a dependency was bypassed outright,
 *                               so this was released rather than satisfied
 *
 * "Bypassable" means an engineer can legitimately work around it: break-glass
 * local accounts for an authentication dependency, a flat recovery VLAN for a
 * network path. The badge is the instruction that one is required — it is not
 * a warning that something is wrong.
 *
 * A COUNT BADGE, NOT A BORDER. The caveat was a 2px amber outline on a 48px
 * saturated square, and amber against the WAIT fill is orange on orange — the
 * two hues are four steps apart and at that weight the border was invisible on
 * every row in the estate. A badge sits proud of the fill, survives both
 * backgrounds, and carries the number, which is the thing an engineer actually
 * needs: "six of these still need working around" is a different job from one.
 */

export const WAITGO_GREEN = 'rgb(151,200,100)'
export const WAITGO_RED = 'rgb(232,96,55)'
export const WAITGO_CAVEAT_BORDER = 'rgb(245,158,11)'

export interface WaitGoCounts {
    waitGo?: string | null
    hardOutstanding?: number | null
    bypassableOutstanding?: number | null
    deferrableOutstanding?: number | null
    /** Dependencies whose own runbook was marked Bypassed — released, not done. */
    bypassedDependencies?: number | null
}

export function isGo(row: WaitGoCounts | string | null | undefined): boolean {
    const v = typeof row === 'string' ? row : row?.waitGo
    return String(v ?? '').toUpperCase() === 'GO'
}

/**
 * A caveat exists when something bypassable is still outstanding, or when a
 * dependency was bypassed outright.
 *
 * The second case matters most on a GO: the object is clear to start because
 * someone decided not to recover what it needs, not because that work is done.
 * Showing that as a plain green square would hide the decision.
 */
export function hasCaveats(row: WaitGoCounts | null | undefined): boolean {
    return Number(row?.bypassableOutstanding ?? 0) > 0
        || Number(row?.bypassedDependencies ?? 0) > 0
}

/** How many caveats the badge shows: things still to work around, plus those already released. */
export function caveatCount(row: WaitGoCounts | null | undefined): number {
    return Number(row?.bypassableOutstanding ?? 0) + Number(row?.bypassedDependencies ?? 0)
}

/**
 * Go, under the reader's own policy about bypassing.
 *
 * The database answers "can this start if bypasses are permitted", because that is what the edge
 * types describe. Command and control may be running strictly — nobody applies a break-glass
 * account or a flat VLAN — and under that policy an object whose only remaining
 * dependencies are bypassable is NOT startable.
 *
 * Only bypassableOutstanding flips the state. A dependency already bypassed was a decision
 * somebody made and acted on; re-blocking on it would ask them to undo a call already taken.
 */
export function effectiveIsGo(row: WaitGoCounts | null | undefined, allowBypass = false): boolean {
    if (!isGo(row)) return false
    return allowBypass || Number(row?.bypassableOutstanding ?? 0) === 0
}

/** Inline style for the square/pill: fill by state, amber border for caveats. */
export function waitGoStyle(row: WaitGoCounts | null | undefined): React.CSSProperties {
    const caveats = hasCaveats(row)
    return {
        backgroundColor: isGo(row) ? WAITGO_GREEN : WAITGO_RED,
        color: '#fff',
        // A 2px amber border rather than a different fill: the fill already
        // carries go/wait, and re-using it for caveats would lose that.
        border: caveats ? `2px solid ${WAITGO_CAVEAT_BORDER}` : '2px solid transparent',
        boxSizing: 'border-box',
    }
}

/** Hover text naming what the caveat actually is. */
export function waitGoTitle(row: WaitGoCounts | null | undefined): string {
    const hard = Number(row?.hardOutstanding ?? 0)
    const bypass = Number(row?.bypassableOutstanding ?? 0)
    const defer = Number(row?.deferrableOutstanding ?? 0)
    const bypassed = Number(row?.bypassedDependencies ?? 0)
    const parts: string[] = []
    if (hard > 0) parts.push(`${hard} ${hard === 1 ? 'dependency' : 'dependencies'} outstanding`)
    if (bypassed > 0) {
        parts.push(
            `${bypassed} ${bypassed === 1 ? 'dependency was' : 'dependencies were'} bypassed — released, not recovered`
        )
    }
    if (bypass > 0) {
        parts.push(
            `${bypass} bypassable (break-glass authentication or a flat recovery VLAN) — proceed only if the bypass is applied`
        )
    }
    if (defer > 0) parts.push(`${defer} deferrable — will run degraded until complete`)
    if (!parts.length) return 'All dependencies complete'
    return parts.join('; ')
}

/** The label: Go/Wait, with "with caveats" appended when one applies. */
export function waitGoLabel(row: WaitGoCounts | null | undefined, goWord = 'GO'): string {
    const base = isGo(row) ? goWord : 'WAIT'
    return hasCaveats(row) ? `${base} *` : base
}


/**
 * The amber caveat badge — a count, sitting proud of the top-right corner of the state button.
 *
 * Rendered by the caller inside a `relative` wrapper so it can overlap the button edge; that
 * overlap is what makes it readable against both fills rather than competing with either.
 */
export function WaitGoCaveatBadge({ row }: { row: WaitGoCounts | null | undefined }) {
    const n = caveatCount(row)
    if (n <= 0) return null
    return (
        <span
            aria-label={`${n} bypassable`}
            className="pointer-events-none absolute -right-1 -top-1 z-10 flex h-[18px] min-w-[18px]
                       items-center justify-center rounded-full px-1 text-[10px] font-bold
                       leading-none text-white shadow ring-2 ring-background tabular-nums"
            style={{ backgroundColor: WAITGO_CAVEAT_BORDER }}
        >
            {n}
        </span>
    )
}
